// src/components/resources/calculators/MarketingBudgetCalculator.related.ts

/**
 * Hand-curated cross-links for the Marketing Budget Calculator's
 * "Related concepts & calculators" section.
 *
 * Kept in a sibling (non-`'use client'`) module so the slugs can be
 * unit-tested against the live glossary + calculator registries
 * (`src/lib/resources/__tests__/related-link-slugs.test.ts`) without
 * pulling the calculator's client-only React deps into the test runtime.
 * A typo or registry rename fails the test instead of shipping a 404 link.
 */

export interface RelatedLinkEntry {
  /** Slug — for glossary: `<category>/<slug>`; for calculators: bare slug. */
  slug: string
  /** Short link text. */
  label: string
  /** One-line explanatory note rendered after the link. */
  note: string
}

export const RELATED_GLOSSARY: ReadonlyArray<RelatedLinkEntry> = [
  { slug: 'metrics/return-on-ad-spend-roas', label: 'ROAS', note: 'Rank channels by return on ad spend, then shift budget toward the top.' },
  { slug: 'metrics/marketing-efficiency-ratio-mer', label: 'MER', note: 'Blended efficiency for judging the whole budget when per-channel ROAS breaks.' },
  { slug: 'metrics/customer-acquisition-cost-cac', label: 'CAC', note: 'Tie channel budgets to the cost of acquiring a customer.' },
  { slug: 'metrics/customer-lifetime-value-clv', label: 'Customer LTV', note: 'LTV:CAC sets how aggressively you can fund acquisition.' },
] as const

export const RELATED_TOOLS: ReadonlyArray<RelatedLinkEntry> = [
  { slug: 'roas-calculator', label: 'ROAS Calculator', note: 'Check the return each channel allocation is expected to produce.' },
  { slug: 'mer-calculator', label: 'MER Calculator', note: 'Measure blended efficiency across your whole budget.' },
  { slug: 'customer-ltv-calculator', label: 'LTV Calculator', note: 'Size sustainable acquisition spend from lifetime value.' },
  { slug: 'creative-testing-calculator', label: 'Creative Testing Calculator', note: 'Budget the experimental reserve your mix sets aside for testing.' },
] as const
